import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Typography, Paper, Chip, CircularProgress, MenuItem, Select,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Collapse, Rating, TableSortLabel,
  Avatar, Tooltip
} from "@mui/material";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, BarChart, Bar, Cell
} from "recharts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import axios from "axios";
import { BACKEND_URL } from "../../utils/apiConfig";
import PageHeader from "../../ui-components/PageHeader";

const CATEGORIES = [
  { key: "briefing", label: "Briefing", color: "#6366f1" },
  { key: "feedback", label: "Feedback", color: "#8b5cf6" },
  { key: "proces", label: "Proces", color: "#ec4899" },
  { key: "eindproduct", label: "Eindproduct", color: "#10b981" },
  { key: "aanrader", label: "Aanrader", color: "#f59e0b" },
];

const MONTHS_NL = [
  "", "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december"
];

// Color for score value
const scoreColor = (v) => {
  if (v == null) return "#9ca3af";
  if (v >= 4.5) return "#10b981";
  if (v >= 4.0) return "#f59e0b";
  if (v >= 3.5) return "#f97316";
  return "#ef4444";
};

const avg = (arr) => {
  const valid = arr.filter((v) => v != null && !isNaN(v));
  if (!valid.length) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
};

export default function EnqueteDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [yearFilter, setYearFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortField, setSortField] = useState("datum");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [searchParams] = useSearchParams();
  const klantFilter = (searchParams.get("klant") || "").trim().toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/Api/enquete`);
        setData(res.data.records || []);
      } catch (err) {
        console.error("Enquete fetch error:", err);
        setError("Kon enquete data niet laden");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Available years
  const years = useMemo(() => {
    const set = new Set(data.map((d) => d.jaar).filter(Boolean));
    return [...set].sort((a, b) => b - a);
  }, [data]);

  // Filtered data
  const filtered = useMemo(() => {
    return data.filter((d) => {
      if (yearFilter !== "all" && d.jaar !== parseInt(yearFilter)) return false;
      if (typeFilter !== "all" && d.type !== typeFilter) return false;
      if (klantFilter) {
        const k = (d.klant || "").toLowerCase();
        if (!k || !(k.includes(klantFilter) || klantFilter.includes(k))) return false;
      }
      return true;
    });
  }, [data, yearFilter, typeFilter, klantFilter]);

  // Previous year data for comparison
  const prevYearData = useMemo(() => {
    if (yearFilter === "all") return [];
    const prevYear = parseInt(yearFilter) - 1;
    return data.filter((d) => d.jaar === prevYear &&
      (typeFilter === "all" || d.type === typeFilter));
  }, [data, yearFilter, typeFilter]);

  // KPI calculations
  const kpis = useMemo(() => {
    const avgScore = avg(filtered.map((d) => d.gemiddelde));
    const prevAvgScore = avg(prevYearData.map((d) => d.gemiddelde));
    const nps = avg(filtered.map((d) => d.aanrader));

    // Find lowest category
    const catAvgs = CATEGORIES.map((c) => ({
      ...c,
      avg: avg(filtered.map((d) => d[c.key])),
    }));
    const lowest = catAvgs
      .filter((c) => c.avg != null)
      .sort((a, b) => a.avg - b.avg)[0];

    return {
      avgScore,
      prevAvgScore,
      diff: avgScore != null && prevAvgScore != null ? avgScore - prevAvgScore : null,
      total: filtered.length,
      nps,
      lowest,
      catAvgs,
    };
  }, [filtered, prevYearData]);

  // Trend data (by month)
  const trendData = useMemo(() => {
    const byMonth = {};
    filtered.forEach((d) => {
      if (!d.jaar || !d.maand) return;
      const monthIdx = MONTHS_NL.indexOf(d.maand.toLowerCase());
      if (monthIdx < 1) return;
      const key = `${d.jaar}-${String(monthIdx).padStart(2, "0")}`;
      if (!byMonth[key]) byMonth[key] = { key, records: [] };
      byMonth[key].records.push(d);
    });

    return Object.values(byMonth)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((m) => {
        const entry = { name: m.key.replace(/^(\d{4})-0?/, "$1-") };
        // Format: "jan '24"
        const [year, month] = m.key.split("-");
        entry.name = `${MONTHS_NL[parseInt(month)].substring(0, 3)} '${year.substring(2)}`;
        CATEGORIES.forEach((c) => {
          entry[c.key] = avg(m.records.map((r) => r[c.key]));
        });
        entry.gemiddelde = avg(m.records.map((r) => r.gemiddelde));
        entry.count = m.records.length;
        return entry;
      });
  }, [filtered]);

  // Radar data
  const radarData = useMemo(() => {
    return CATEGORIES.map((c) => ({
      subject: c.label,
      score: avg(filtered.map((d) => d[c.key])) || 0,
      fullMark: 5,
    }));
  }, [filtered]);

  // Score breakdown bar data
  const breakdownData = useMemo(() => {
    return CATEGORIES.map((c) => ({
      name: c.label,
      score: avg(filtered.map((d) => d[c.key])),
      color: c.color,
    }));
  }, [filtered]);

  // Sorted table data
  const sortedData = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === "datum") {
        aVal = aVal || "";
        bVal = bVal || "";
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      aVal = String(aVal || "");
      bVal = String(bVal || "");
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return sorted.slice(0, 30);
  }, [filtered, sortField, sortDir]);

  // Comments feed
  const comments = useMemo(() => {
    const all = [];
    filtered.forEach((d) => {
      const fields = [
        { field: "opmerking_proces", label: "Proces" },
        { field: "opmerking_eindproduct", label: "Eindproduct" },
        { field: "opmerking_aanrader", label: "Aanrader" },
        { field: "opmerking_slot", label: "Tot slot" },
      ];
      fields.forEach(({ field, label }) => {
        if (d[field] && d[field].trim()) {
          all.push({
            text: d[field],
            label,
            klant: d.klant,
            project: d.project || d.projectnaam,
            score: d.gemiddelde,
            datum: d.datum,
            type: d.type,
          });
        }
      });
    });
    return all.sort((a, b) => (b.datum || "").localeCompare(a.datum || "")).slice(0, 20);
  }, [filtered]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1800, mx: "auto", px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header + Filters */}
      <PageHeader
        icon="star"
        title="Klanttevredenheid"
        subtitle={`${filtered.length} enquetes${yearFilter !== "all" ? ` in ${yearFilter}` : ""}${typeFilter !== "all" ? ` (${typeFilter})` : ""}${klantFilter ? ` · klant: ${searchParams.get("klant")}` : ""}`}
        rightActions={(
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Jaar</InputLabel>
              <Select value={yearFilter} label="Jaar" onChange={(e) => setYearFilter(e.target.value)}>
                <MenuItem value="all">Alles</MenuItem>
                {years.map((y) => (
                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                <MenuItem value="all">Alles</MenuItem>
                <MenuItem value="Video">Video</MenuItem>
                <MenuItem value="Animatie">Animatie</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      />

      {/* KPI Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
        <KPICard
          title="Gemiddelde Score"
          value={kpis.avgScore != null ? kpis.avgScore.toFixed(1) : "-"}
          subtitle={kpis.diff != null ? `${kpis.diff >= 0 ? "+" : ""}${kpis.diff.toFixed(2)} vs vorig jaar` : ""}
          icon={<StarIcon />}
          color="#f59e0b"
          rating={kpis.avgScore}
          trend={kpis.diff}
        />
        <KPICard
          title="Totaal Enquetes"
          value={kpis.total}
          subtitle={yearFilter !== "all" ? `in ${yearFilter}` : "totaal"}
          icon={<AssessmentIcon />}
          color="#6366f1"
        />
        <KPICard
          title="Aanrader Score"
          value={kpis.nps != null ? kpis.nps.toFixed(1) : "-"}
          subtitle="Vraag 5: zou ons aanraden"
          icon={<StarIcon />}
          color="#10b981"
          rating={kpis.nps}
        />
        <KPICard
          title="Pijnpunt"
          value={kpis.lowest ? kpis.lowest.avg?.toFixed(1) : "-"}
          subtitle={kpis.lowest ? kpis.lowest.label : ""}
          icon={<TrendingDownIcon />}
          color="#ef4444"
          rating={kpis.lowest?.avg}
        />
      </Box>

      {/* Trend + Radar Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2, mb: 3 }}>
        {/* Trend Chart */}
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Score Trend per Maand
          </Typography>
          {trendData.length > 1 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis domain={[1, 5]} fontSize={12} />
                <RTooltip
                  formatter={(val, name) => [val?.toFixed(2), name]}
                  labelFormatter={(l) => `${l}`}
                />
                <Legend />
                {CATEGORIES.map((c) => (
                  <Line
                    key={c.key}
                    type="monotone"
                    dataKey={c.key}
                    name={c.label}
                    stroke={c.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography color="text.secondary">Selecteer een periode met meerdere maanden</Typography>
            </Box>
          )}
        </Paper>

        {/* Radar Chart */}
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Sterkte/Zwakte Profiel
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="subject" fontSize={12} />
              <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
              <Radar
                name="Score"
                dataKey="score"
                stroke="#6366f1"
                fill="#6366f1"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Score Breakdown */}
      <Paper sx={{ p: 2, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Score per Categorie
        </Typography>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={breakdownData} layout="vertical" margin={{ left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" domain={[0, 5]} fontSize={12} />
            <YAxis type="category" dataKey="name" fontSize={13} width={80} />
            <RTooltip formatter={(val) => [val?.toFixed(2), "Score"]} />
            <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={24}>
              {breakdownData.map((entry, idx) => (
                <Cell key={idx} fill={scoreColor(entry.score)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Recent Enquetes Table */}
      <Paper sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Recente Enquetes
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "#f9fafb" } }}>
                <TableCell width={40} />
                <TableCell>
                  <TableSortLabel active={sortField === "project"} direction={sortField === "project" ? sortDir : "asc"} onClick={() => handleSort("project")}>
                    Project
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === "klant"} direction={sortField === "klant" ? sortDir : "asc"} onClick={() => handleSort("klant")}>
                    Klant
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel active={sortField === "gemiddelde"} direction={sortField === "gemiddelde" ? sortDir : "asc"} onClick={() => handleSort("gemiddelde")}>
                    Score
                  </TableSortLabel>
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>
                  <TableSortLabel active={sortField === "datum"} direction={sortField === "datum" ? sortDir : "asc"} onClick={() => handleSort("datum")}>
                    Datum
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer", "& td": { borderBottom: expandedRow === row.id ? "none" : undefined } }}
                    onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedRow === row.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                        {row.project || row.projectnaam || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.klant || "-"}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.gemiddelde != null ? row.gemiddelde.toFixed(1) : "-"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: `${scoreColor(row.gemiddelde)}20`,
                          color: scoreColor(row.gemiddelde),
                          border: `1px solid ${scoreColor(row.gemiddelde)}40`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={row.type || "-"} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.datum ? new Date(row.datum).toLocaleDateString("nl-NL") : "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0 }}>
                      <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                        <ExpandedDetail row={row} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Comments Feed */}
      {comments.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Recente Opmerkingen
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {comments.map((c, i) => (
              <Box
                key={i}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f9fafb",
                  borderLeft: `3px solid ${scoreColor(c.score)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <FormatQuoteIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {c.klant} — {c.project}
                  </Typography>
                  <Chip label={c.label} size="small" variant="outlined" sx={{ ml: "auto", height: 20, fontSize: 11 }} />
                  {c.score != null && (
                    <Chip
                      label={c.score.toFixed(1)}
                      size="small"
                      sx={{
                        height: 20, fontSize: 11, fontWeight: 600,
                        bgcolor: `${scoreColor(c.score)}20`,
                        color: scoreColor(c.score),
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontStyle: "italic", color: "#374151" }}>
                  "{c.text}"
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// KPI Card Component
function KPICard({ title, value, subtitle, icon, color, rating, trend }) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: color,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ my: 0.5, color }}>
            {value}
          </Typography>
          {rating != null && (
            <Rating value={rating} precision={0.1} readOnly size="small" sx={{ mb: 0.5 }} />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {trend != null && trend !== 0 && (
              trend > 0 ? (
                <TrendingUpIcon sx={{ fontSize: 14, color: "#10b981" }} />
              ) : (
                <TrendingDownIcon sx={{ fontSize: 14, color: "#ef4444" }} />
              )
            )}
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 44, height: 44 }}>
          {icon}
        </Avatar>
      </Box>
    </Paper>
  );
}

// Expanded detail for a survey row
function ExpandedDetail({ row }) {
  const scores = [
    { label: "Briefing", value: row.briefing },
    { label: "Feedback", value: row.feedback },
    { label: "Proces", value: row.proces },
    { label: "Eindproduct", value: row.eindproduct },
    { label: "Aanrader", value: row.aanrader },
    { label: "Storyboard", value: row.storyboard },
  ];

  const comments = [
    { label: "Proces", text: row.opmerking_proces },
    { label: "Eindproduct", text: row.opmerking_eindproduct },
    { label: "Aanrader", text: row.opmerking_aanrader },
    { label: "Tot slot", text: row.opmerking_slot },
  ].filter((c) => c.text && c.text.trim());

  const team = [
    row.creative && `Creative: ${row.creative}`,
    row.cameraman && `Camera: ${row.cameraman}`,
    row.editor && `Editor: ${row.editor}`,
    row.animator && `Animator: ${row.animator}`,
  ].filter(Boolean);

  return (
    <Box sx={{ p: 2, bgcolor: "#f9fafb" }}>
      {/* Scores */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        {scores.map((s) =>
          s.value != null ? (
            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{s.label}:</Typography>
              <Chip
                label={s.value}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: `${scoreColor(s.value)}15`,
                  color: scoreColor(s.value),
                  minWidth: 32,
                }}
              />
            </Box>
          ) : null
        )}
      </Box>

      {/* Team */}
      {team.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {team.join(" | ")}
        </Typography>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {comments.map((c, i) => (
            <Box key={i} sx={{ pl: 1, borderLeft: "2px solid #d1d5db" }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {c.label}
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                "{c.text}"
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Contact */}
      {row.contactpersoon && (
        <Typography variant="caption" color="text.secondary" mt={1} display="block">
          Contactpersoon: {row.contactpersoon}
        </Typography>
      )}
    </Box>
  );
}
